import React, { useState } from 'react';
import { useWebsiteContent } from '../context/WebsiteContentContext';
import Layout from '../components/Layout';
import './WebsiteContent.css';

const ContentField = ({ block, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(block.content || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    const result = await onSave(block.id, content);
    setIsSaving(false);
    if (result.success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setContent(block.content || '');
    setIsEditing(false);
  };

  const isBoolean = block.content_type === 'boolean';
  const boolValue = content === 'true';

  const handleBooleanToggle = async () => {
    const newValue = !boolValue;
    setContent(newValue ? 'true' : 'false');
    setIsSaving(true);
    const result = await onSave(block.id, newValue ? 'true' : 'false');
    setIsSaving(false);
    if (result.success) {
      setIsEditing(false);
    }
  };

  return (
    <div className="content-field">
      <div className="content-field-header">
        <label className="content-field-label">{block.field_label}</label>
        {block.help_text && <span className="content-field-help">{block.help_text}</span>}
      </div>
      {isBoolean ? (
        <div className="content-field-display content-field-display--boolean">
          <div className="content-preview">
            <span className={`content-boolean content-boolean--${boolValue ? 'on' : 'off'}`}>
              {boolValue ? 'On' : 'Off'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleBooleanToggle}
            className="edit-btn"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : (boolValue ? 'Disable' : 'Enable')}
          </button>
        </div>
      ) : isEditing ? (
        <div className="content-field-edit">
          {block.content_type === 'textarea' ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="content-textarea"
              maxLength={block.max_length || undefined}
            />
          ) : (
            <input
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="content-input"
              maxLength={block.max_length || undefined}
            />
          )}
          {block.max_length && (
            <div className="content-char-count">
              {content.length} / {block.max_length}
            </div>
          )}
          <div className="content-field-actions">
            <button onClick={handleCancel} className="cancel-btn" disabled={isSaving}>
              Cancel
            </button>
            <button onClick={handleSave} className="save-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="content-field-display">
          <div className="content-preview">
            {block.content || <em className="content-empty">No content set</em>}
          </div>
          <button onClick={() => setIsEditing(true)} className="edit-btn">
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

const SectionGroup = ({ page, section, blocks, onSave }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const getSectionTitle = (section) => {
    const titles = {
      hero: 'Hero Section',
      services: 'Services Overview',
      'why-choose-us': 'Why Choose Us',
      cta: 'Call to Action',
      'about-hero': 'Hero Section',
      'about-content': 'About Content',
      'vendors-hero': 'Hero Section',
      'contact-hero': 'Hero Section',
      'contact-info': 'Contact Information',
      navbar: 'Navbar',
    };
    return titles[section] || section;
  };

  return (
    <div className="section-group">
      <button className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
        >
          <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
        <span>{getSectionTitle(section)}</span>
        <span className="section-count">{blocks.length} fields</span>
      </button>
      {isExpanded && (
        <div className="section-content">
          {blocks.map((block) => (
            <ContentField key={block.id} block={block} onSave={onSave} />
          ))}
        </div>
      )}
    </div>
  );
};

const PageTab = ({ page, isActive, onClick }) => {
  const getPageTitle = (page) => {
    const titles = {
      homepage: 'Home',
      about: 'About',
      contact: 'Contact',
      vendors: 'Vendrs',
      website: 'Website',
    };
    return titles[page] || page;
  };

  return (
    <button className={`page-tab ${isActive ? 'active' : ''}`} onClick={onClick}>
      {getPageTitle(page)}
    </button>
  );
};

const WebsiteContent = () => {
  const { contentBlocks, updateContent, loading } = useWebsiteContent();
  const [activePage, setActivePage] = useState('homepage');

  // Define page order
  const pageOrder = ['homepage', 'about', 'vendors', 'contact', 'website'];

  // Define section order for each page (matching website layout)
  const sectionOrder = {
    homepage: ['hero', 'services', 'why-choose-us', 'cta'],
    about: ['about-hero', 'about-content'],
    vendors: ['vendors-hero'],
    contact: ['contact-hero', 'contact-info'],
    website: ['navbar'],
  };
  
  // Get unique pages from content blocks and sort by the defined order
  const uniquePages = [...new Set(contentBlocks.map((block) => block.page))];
  const pages = pageOrder.filter(page => uniquePages.includes(page));
  
  const pageContent = contentBlocks.filter((block) => block.page === activePage);
  const uniqueSections = [...new Set(pageContent.map((block) => block.section))];
  
  // Sort sections by the defined order for the active page
  const sections = sectionOrder[activePage] 
    ? sectionOrder[activePage].filter(section => uniqueSections.includes(section))
    : uniqueSections;

  const handleSave = async (id, content) => {
    return await updateContent(id, content);
  };

  if (loading && contentBlocks.length === 0) {
    return (
      <Layout>
        <div className="website-content">
          <div className="content-loading">Loading website content...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="website-content">
        <div className="content-header">
          <h1>Website Content</h1>
          <p className="content-subtitle">
            Edit your website's text content. Changes are saved individually and will be reflected on
            the live website.
          </p>
        </div>

        {contentBlocks.length === 0 ? (
          <div className="content-empty-state">
            <p>No content blocks found. Please run the database migration to set up content blocks.</p>
          </div>
        ) : (
          <>
            <div className="page-tabs">
              {pages.map((page) => (
                <PageTab
                  key={page}
                  page={page}
                  isActive={activePage === page}
                  onClick={() => setActivePage(page)}
                />
              ))}
            </div>

            <div className="content-sections">
              {sections.map((section) => {
                const sectionBlocks = pageContent.filter((block) => block.section === section);
                return (
                  <SectionGroup
                    key={section}
                    page={activePage}
                    section={section}
                    blocks={sectionBlocks}
                    onSave={handleSave}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default WebsiteContent;

